import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { Router } from '@angular/router';
import { 
  IdentityWebApiClient,
  ListApiScopesResponse,
  ListApiScopesRequest,
  CreateApiClientRequest,
  CreateApiClientResponse
} from 'identity-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigService } from 'src/app/services/config.service';
import { MatDialog } from '@angular/material/dialog';
import { SituApiActionConfirmationDialogComponent } from '../situapi-action-confirmation-dialog/situapi-action-confirmation-dialog.component';

@Component({
  selector: 'situapi-add-client',
  templateUrl: './situapi-add-client.component.html',
  styleUrls: ['./situapi-add-client.component.less']
})
export class SituApiAddClientComponent implements OnInit, OnDestroy {
  admin = false;
  companyAdmin = false;
  scopes: string[] | undefined;
  companyId: string | undefined;
  clientForm: FormGroup;
  submitted = false;

  private _subscriptions: Subscription[] = [];

  private _showLoading = false;
  public get showLoading(): boolean {
    return this._showLoading;
  }

  public set showLoading(val: boolean) {
    this._showLoading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private identityWebApiClient: IdentityWebApiClient,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private dialog: MatDialog) {
      this.clientForm = this.formBuilder.group({
        clientName: [null, Validators.required],
        scopes: [null, Validators.required]
      });
    }

  ngOnInit(): void {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
    this.companyAdmin = (this.authService.hasRole(Role.BuilderAdmin) || this.authService.hasRole(Role.Admin));
    this._subscriptions.push(this.stateService.state$.subscribe(x => {
      this.companyId = x.company?.id ?? undefined;
      if (this.admin) {
        this.loadScopes();
      } else {
        this.clientForm.setValue({
          clientName: `${x.company?.code?.toLowerCase()}-${this.configService.config.envName.toLowerCase()}`,
          scopes: ["SituApi"]
        });
      }
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  get form() { return this.clientForm.controls; }

  onSubmit() {
    this.submitted = true;

    if (this.clientForm.invalid) {
      this.submitted = false;
      return;
    }

    let request = this.clientForm.value as CreateApiClientRequest;
    request.companyId = this.companyId;
    this.addClient(request);
  }

  showClientsView() {
    this.router.navigate(['/docs/situapi/manage-clients']);
  }

  private loadScopes() {
    this.showLoading = true;

    let request = new ListApiScopesRequest();

    return this.identityWebApiClient.listApiScopes(request)
      .then((response: any) => {
        let listApiScopesResponse = response as ListApiScopesResponse;
        if (listApiScopesResponse.success) {
          this.scopes = listApiScopesResponse.scopes?.map(x => x.scope ?? "");
        } else {
          if (listApiScopesResponse?.errorMessages) {
            this.showMessage(listApiScopesResponse.errorMessages.join("\r\n"));
          }
        }
        this.showLoading = false;
      })
      .catch((err: any) => {
        this.showMessage("Failed to Fetch Api Scopes!");
        this.showLoading = false;
      });
  }

  private addClient(request: CreateApiClientRequest) {
    return this.identityWebApiClient.createApiClient(request)
      .then((response: any) => {
        let createApiClientsResponse = response as CreateApiClientResponse;
        if (createApiClientsResponse.success) {
          let dialogRef = this.dialog.open(SituApiActionConfirmationDialogComponent, {
            width: '450px',
            data: { 
              oneTimeApiKey: createApiClientsResponse.oneTimeApiKey,
              clientName: request.clientName }
          });

          dialogRef.afterClosed().subscribe(result => {
            this.router.navigate(["/docs/situapi/manage-clients"]);
          });
        } else {
          if (createApiClientsResponse?.errorMessages) {
            this.showMessage(createApiClientsResponse.errorMessages.join("\r\n"));
          }
        }
        this.showLoading = false;
      })
      .catch((err: any) => {
        this.showMessage("Failed to Create Api Client!");
        this.showLoading = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }
}
